import React from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';

import { GlassButton } from '../ui/GlassButton';
import { GlassCard } from '../ui/GlassCard';
import { colors, spacing, typography } from '../../core/theme/tokens';
import { useAuth } from '../../contexts/AuthContext';
import { useImportOnboarding } from '../../contexts/ImportOnboardingContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { navigateToAuth } from '../../core/navigation/navigationRef';
import { formatCurrency } from '../charts/d3/format';
import type { InvestmentHoldingRow } from '../../hooks/useInvestmentCompounding';
import { ONBOARDING_STEP } from '../../types/onboarding';

type InvestmentHoldingsTableProps = {
  holdings: InvestmentHoldingRow[];
  principal: number;
  weightedRatePct: number;
  annualContribution: number;
};

function formatRate(pct: number): string {
  const rounded = Math.round(pct * 10) / 10;
  return `${rounded}%`;
}

export function InvestmentHoldingsTable({
  holdings,
  principal,
  weightedRatePct,
  annualContribution,
}: InvestmentHoldingsTableProps) {
  const { isAuthenticated } = useAuth();
  const { openImportMore } = useImportOnboarding();
  const { reopen } = useOnboarding();

  const handleEnterManually = async () => {
    if (!isAuthenticated) {
      navigateToAuth();
      return;
    }
    await reopen({
      completedIntro: true,
      step: ONBOARDING_STEP.ASSETS,
    });
  };

  if (holdings.length === 0) {
    return (
      <GlassCard style={styles.card}>
        <Text style={styles.title}>Investments</Text>
        <Text style={styles.hint}>
          Add brokerage balances with import, or enter them manually.
        </Text>
        <GlassButton
          variant="primary"
          size="sm"
          onPress={openImportMore}
          accessibilityLabel="Import statements and investment screenshots"
        >
          Import statements & screenshots
        </GlassButton>
        <GlassButton
          variant="ghost"
          size="sm"
          onPress={() => {
            void handleEnterManually();
          }}
          accessibilityLabel="Enter investment balances manually"
        >
          Enter manually
        </GlassButton>
      </GlassCard>
    );
  }

  return (
    <GlassCard style={styles.card}>
      <Text style={styles.title}>Investment holdings</Text>
      <Text style={styles.hint}>
        {formatCurrency(principal)} invested · blended expected return {formatRate(weightedRatePct)}
        {annualContribution > 0
          ? ` · ${formatCurrency(annualContribution)}/yr planned contributions`
          : ''}
      </Text>

      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <View>
          <View style={styles.headerRow}>
            <Text style={[styles.headerCell, styles.colAccount]}>Account</Text>
            <Text style={[styles.headerCell, styles.colNum]}>Balance</Text>
            <Text style={[styles.headerCell, styles.colRate]}>Return</Text>
            <Text style={[styles.headerCell, styles.colShare]}>Share</Text>
          </View>
          {holdings.map((row) => (
            <View key={row.id} style={styles.dataRow}>
              <Text style={[styles.cell, styles.colAccount]} numberOfLines={1}>
                {row.label}
              </Text>
              <Text style={[styles.cell, styles.colNum, styles.tabular]}>
                {formatCurrency(row.value)}
              </Text>
              <Text style={[styles.cell, styles.colRate, styles.rate]}>
                {formatRate(row.expectedReturnPct)}
              </Text>
              <Text style={[styles.cell, styles.colShare, styles.tabular]}>{row.sharePct}%</Text>
            </View>
          ))}
          <View style={[styles.dataRow, styles.totalRow]}>
            <Text style={[styles.cell, styles.colAccount, styles.totalText]}>Total</Text>
            <Text style={[styles.cell, styles.colNum, styles.tabular, styles.totalText]}>
              {formatCurrency(principal)}
            </Text>
            <Text style={[styles.cell, styles.colRate, styles.rate, styles.totalText]}>
              {formatRate(weightedRatePct)}
            </Text>
            <Text style={[styles.cell, styles.colShare, styles.tabular, styles.totalText]}>
              100%
            </Text>
          </View>
        </View>
      </ScrollView>
    </GlassCard>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.sm,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.xs,
  },
  headerRow: {
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
    paddingBottom: spacing.xs,
    marginBottom: spacing.xs,
  },
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  totalRow: {
    borderBottomWidth: 0,
    marginTop: spacing.xs,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.glassBorder,
  },
  headerCell: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
    fontSize: 11,
  },
  cell: {
    ...typography.caption,
    color: colors.text,
  },
  colAccount: {
    width: 120,
    paddingRight: spacing.sm,
  },
  colNum: {
    width: 96,
    textAlign: 'right',
  },
  colRate: {
    width: 64,
    textAlign: 'right',
  },
  colShare: {
    width: 56,
    textAlign: 'right',
  },
  tabular: {
    fontVariant: ['tabular-nums'],
  },
  rate: {
    color: colors.accent,
    fontVariant: ['tabular-nums'],
  },
  totalText: {
    fontWeight: '700',
    color: colors.text,
  },
});
